import { Header } from '@components/Header';
import { useAuthContext } from '@providers/AuthProvider';
import { useModalContext } from '@providers/ModalProvider';
import { Restricted } from '@providers/PermissionProvider/Restricted';
import { useAPI } from '@providers/useAPI';
import { routes } from '@routes';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiKeysKeys } from 'app/queries/apiKeys/apiKeysKeys';
import { EPermission } from 'app/types/enums/EPermission';
import { ModalNamesEnum } from 'app/types/enums/ModalNamesEnum';
import { useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { AuthCallout } from '@pages/Admin/ApiDocsPages/components/AuthCallout';
import { createFetchOrganizationApiKeysQueryFn } from 'app/queries/apiKeys/createFetchOrganizationApiKeysQueryFn';
import { ApiKeyPanel } from './components/ApiKeyPanel';
import { EndpointTester } from './components/EndpointTester';
import { ENDPOINTS } from './endpoints';
import { sortApiKeysByNewestFirst } from './helpers/sortApiKeysByNewestFirst';

export const OrganizationApiDocsPage = () => {
  const { organization } = useAuthContext();
  const { call } = useAPI();
  const queryClient = useQueryClient();
  const { openModal, closeModal } = useModalContext();

  const orgId = organization?.id ?? '';
  const orgSlug = organization?.slug ?? '';

  const [pickedKeyId, setPickedKeyId] = useState('');

  const keysQuery = useQuery({
    queryKey: apiKeysKeys.list(orgId),
    queryFn: createFetchOrganizationApiKeysQueryFn(call),
    enabled: orgId !== '',
  });

  const keys = useMemo(() => sortApiKeysByNewestFirst(keysQuery.data ?? []), [keysQuery.data]);
  const selectedKeyId = keys.some((key) => key.id === pickedKeyId) ? pickedKeyId : keys[0]?.id ?? '';

  const createKey = () => {
    openModal(ModalNamesEnum.CreateAPIKey, {
      onSuccess: () => {
        closeModal();
        void queryClient.invalidateQueries({ queryKey: apiKeysKeys.list(orgId) });
      },
    });
  };

  return (
    <>
      <Helmet>
        <title>Organization API Docs - AutoScript</title>
      </Helmet>
      <Header title="Organization API" backRoute={orgSlug ? routes.organizationDetails.make(orgSlug) : undefined} />
      <Restricted
        to={EPermission.manageOrganization}
        fallback={
          <div className="mx-auto max-w-4xl px-6 py-6 text-sm text-[var(--color-text-subtle)]">
            You need the Manage Organization permission to view the API documentation.
          </div>
        }
      >
        <div className="mx-auto max-w-4xl px-6 py-6">
          <p className="mb-6 text-sm text-[var(--color-text-subtle)]">
            The AutoScript public API lets your organization create and manage transcription jobs programmatically. All
            requests are sent to the AutoScript API host and authenticated with an Organization API Access Key in the{' '}
            <code className="font-mono text-xs">api-key</code> header — no user session is required.
          </p>

          <AuthCallout>
            Every request must send an Organization API Access Key in the{' '}
            <code className="font-mono text-xs">api-key</code> header. Keys are issued per organization and may carry an
            expiration date. The key must belong to the organization referenced by the{' '}
            <code className="font-mono text-xs">oid</code> path parameter, and any workspace or job referenced in the
            path must belong to that same organization, otherwise the request is rejected with{' '}
            <code className="font-mono text-xs">403 Forbidden</code>.
          </AuthCallout>

          <ApiKeyPanel
            keys={keys}
            selectedKeyId={selectedKeyId}
            onSelect={setPickedKeyId}
            onCreateKey={createKey}
            loading={keysQuery.isFetching}
          />

          {ENDPOINTS.map((endpoint) => (
            <EndpointTester key={endpoint.id} endpoint={endpoint} apiKey={selectedKeyId} orgId={orgId} />
          ))}
        </div>
      </Restricted>
    </>
  );
};
